<script>
import { mapState } from 'vuex';
import { ERROR_CODES } from '@/util/errorCodes';
import SocialBookingSlotService from '@/services/SocialBookingSlotsService';
import SocialBookingSlotsCalendar from '@/views/socialBookingSlots/SocialBookingSlotsCalendar.vue';
import VisitLocationsService from '@/services/VisitLocationsService';

const LOCAL_STORAGE_FILTERS = {
  DAY: 'bookingSlotsDay',
  STATUS: 'bookingSlotsStatus',
};

export default {
  name: 'BookingSlotsTable',
  components: {
    SocialBookingSlotsCalendar,
  },
  data() {
    return {
      loading: true,
      calendarView: false,
      bookingSlots: [],
      weekdays: SocialBookingSlotService.getWeekdays(),
      columns: [
        {
          key: 'day_of_week',
          label: 'Day',
        },
        {
          key: 'visit_location',
          label: 'Location',
        },
        {
          key: 'time',
          label: 'Slot Time',
        },
        {
          key: 'start_date',
          label: 'Start Date',
        },
        {
          key: 'end_date',
          label: 'End Date',
        },
        {
          key: 'actions',
          label: 'Actions',
        },
      ],
      currentPage: 1,
      perPage: 10,
      filters: {
        day: null,
        status: 'active',
        visit_location_id: '',
      },
      visitLocations: [],
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      prison: state => state.prison.prison,
    }),
    filteredBookingSlots() {
      let filteredBookingSlots = this.bookingSlots || [];
      if (this.filters.day && this.filters.day !== 'All') {
        filteredBookingSlots = filteredBookingSlots.filter(bs => bs.day_of_week.toLowerCase() === this.filters.day.toLowerCase());
      }
      if (this.filters.status && this.filters.status !== 'all') {
        filteredBookingSlots = filteredBookingSlots.filter(bs => (this.filters.status === 'active' ? bs.active === true : bs.active === false));
      }
      if (this.filters.visit_location_id) {
        filteredBookingSlots = filteredBookingSlots.filter(bs => (bs.visit_location != null ? bs.visit_location.visit_location_id === this.filters.visit_location_id : false));
      }
      return filteredBookingSlots;
    },
    totalRows() {
      return this.filteredBookingSlots.length;
    },
  },
  async mounted() {
    await this.$store.dispatch('prison/getPrison');
    this.setCalendarViewFromLocal();
    await this.getVisitLocations();
    this.getBookingSlots();
  },
  methods: {
    async getVisitLocations() {
      try {
        const response = await VisitLocationsService.getVisitLocationsForPrison();
        this.visitLocations = response.data;
      } catch (e) {
        console.error('Error getting visit locations: ', e);
        this.$toast.error('Unable to get visit locations. Please refresh the page and try again', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      }
    },
    setCalendarViewFromLocal() {
      const view = localStorage.getItem('calendarView');

      if (view && view === 'true') {
        this.showCalendarView(true);
      }
    },
    showCalendarView(value) {
      this.calendarView = value;
      localStorage.setItem('calendarView', value);
      if (value) {
        this.filters.day = 'All';
      }
    },
    async getBookingSlots() {
      this.loading = true;

      const slots = await this.$store.dispatch('socialBookingSlots/getBookingSlots');
      let mondaySlots = [];
      let tuesdaySlots = [];
      let wednesdaySlots = [];
      let thursdaySlots = [];
      let fridaySlots = [];
      let saturdaySlots = [];
      let sundaySlots = [];
      // Add slots to their days
      slots.forEach(bs => {
        switch (bs.day_of_week.toLowerCase()) {
          case 'monday':
            mondaySlots.push(bs);
            break;
          case 'tuesday':
            tuesdaySlots.push(bs);
            break;
          case 'wednesday':
            wednesdaySlots.push(bs);
            break;
          case 'thursday':
            thursdaySlots.push(bs);
            break;
          case 'friday':
            fridaySlots.push(bs);
            break;
          case 'saturday':
            saturdaySlots.push(bs);
            break;
          case 'sunday':
            sundaySlots.push(bs);
            break;
          default:
            console.error('Cant read day of week for ', bs);
            break;
        }
      });
      // Sort each booking slot based on date
      mondaySlots = mondaySlots.sort(this.sortStartTime);
      tuesdaySlots = tuesdaySlots.sort(this.sortStartTime);
      wednesdaySlots = wednesdaySlots.sort(this.sortStartTime);
      thursdaySlots = thursdaySlots.sort(this.sortStartTime);
      fridaySlots = fridaySlots.sort(this.sortStartTime);
      saturdaySlots = saturdaySlots.sort(this.sortStartTime);
      sundaySlots = sundaySlots.sort(this.sortStartTime);
      this.bookingSlots = [
        ...mondaySlots,
        ...tuesdaySlots,
        ...wednesdaySlots,
        ...thursdaySlots,
        ...fridaySlots,
        ...saturdaySlots,
        ...sundaySlots,
      ];
      this.loading = false;
    },
    sortStartTime(bs1, bs2) {
      const bs1Date = new Date();
      bs1Date.setHours(bs1.slot_begin.split(':')[0], bs1.slot_begin.split(':')[1], 0, 0);
      const bs2Date = new Date();
      bs2Date.setHours(bs2.slot_begin.split(':')[0], bs2.slot_begin.split(':')[1], 0, 0);
      return bs1Date - bs2Date;
    },
    saveFilters() {
      if (this.filters.day && this.filters.day.trim().length > 0) {
        localStorage.setItem(LOCAL_STORAGE_FILTERS.DAY, this.filters.day);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_FILTERS.DAY);
      }
      if (this.filters.status && this.filters.status.trim().length > 0) {
        localStorage.setItem(LOCAL_STORAGE_FILTERS.STATUS, this.filters.status);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_FILTERS.STATUS);
      }
      console.log('Visit location ID:', this.filters.visit_location_id);
    },
    deleteSlot(item) {
      this.$swal({
        title: 'Are you sure?',
        text: `Are you sure you want to delete slot ${item.name}?`,
        icon: 'info',
        buttons: true,
      }).then(value => {
        if (value) {
          this.setBookingSetInactive(item);
        }
      });
    },
    setBookingSetInactive(slot) {
      const slotClone = { ...slot };
      slotClone.active = false;

      SocialBookingSlotService.updateBookingSlot(slotClone._id, slotClone).then(() => {
        this.$toast.success('Booking slot successfully deleted', {
          toastClassName: ['toast-std', 'success-toast'],
        });
        this.getBookingSlots(); // refresh the slots
      }).catch(err => {
        console.log('Error deleting booking slot ', err.response);
        // eslint-disable-next-line max-len
        if (err && err.response && err.response.data && err.response.data.error === ERROR_CODES.BOOKING_SLOTS.OVERLAP) {
          this.displayBookingSlotOverlapModal();
        } else {
          this.$toast.error('Error deleting the booking slot', {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        }
      });
    },
    removeAllSlots() {
      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to delete all slots?',
        icon: 'info',
        buttons: true,
      }).then(value => {
        if (value) {
          SocialBookingSlotService.setAllBookingSlotsInactive(this.prison.prison_id).then(() => {
            this.$toast.success('Successfully deleted all booking slots', {
              toastClassName: ['toast-std', 'success-toast'],
            });
            this.getBookingSlots(); // refresh booking slots
          }).catch(err => {
            console.log('error deleting booking slots', err);
            this.$toast.error('Error deleting all booking slots', {
              toastClassName: ['toast-std', 'warning-toast'],
            });
          });
        }
      });
    },
    editSlot(slot) {
      this.$router.push({ name: 'update-social-booking-slot', params: { bookingSlotId: slot._id } })
    },
  },
};
</script>

<template>
  <div class="theme-color-wrapper p-2 booking-slots-page">
    <div class="calls row">
      <section class="col-12 mb-2">
        <h2 class="font-weight-bolder mb-1">Social Visit Booking Slots</h2>
        <router-link :to="{ name: 'dashboard' }" class="body-text">
          &laquo; Back to dashboard
        </router-link>
      </section>
      <div class="col-12">
        <section v-if="!loading">
          <b-row>
            <b-col cols="3">
              <div class="form-group">
                <label>Location</label>
                <select v-model="filters.visit_location_id" class="form-control form-control-sm" @change="saveFilters">
                  <option value="">All</option>
                  <option v-for="(location, index) in visitLocations" :key="index" :value="location._id">
                    {{ location.name }}
                  </option>
                </select>
              </div>
            </b-col>
            <b-col v-if="!calendarView" cols="3">
              <div class="form-group">
                <label>Day of week</label>
                <select v-model="filters.day" class="form-control form-control-sm" cy="slots-filter-day" @change="saveFilters">
                  <option value="All">All</option>
                  <option v-for="(day, index) in weekdays" :key="index" :value="day">{{ day }}</option>
                </select>
              </div>
            </b-col>
          </b-row>
          <b-row class="my-2">
            <b-col cols="6" class="d-flex align-items-center">
              <span v-if="calendarView" class="d-block pointer" cy="list-view" @click="showCalendarView(false)">
                <i class="fas fa-list" /> List View
              </span>
              <span v-if="!calendarView" class="d-block pointer" cy="calendar-view" @click="showCalendarView(true)">
                <i class="fas fa-list" /> Calendar View
              </span>
            </b-col>
            <b-col cols="6" class="d-flex justify-content-end">
              <router-link :to="{name: 'create-social-booking-slot', params: {prisonId: prison.prison_id}}"
                           class="btn btn-primary mr-1"
              >Add new slot
              </router-link>

              <!--TODO: Complete this-->
              <!--<button class="btn btn-danger" @click="removeAllSlots">Remove all slots</button>-->
            </b-col>
          </b-row>

          <b-container v-if="!calendarView" class="px-0">
            <b-table
              :current-page="currentPage"
              :fields="columns"
              :items="filteredBookingSlots"
              :per-page="perPage"
              :responsive="true"
              class="data-table"
              cy="slot-table"
              hover
            >
              <template #cell(visit_location)="data">
                {{ data.item.visit_location ? data.item.visit_location.name : 'All' }}
              </template>

              <template #cell(time)="data">
                {{ data.item.slot_begin }} - {{ data.item.slot_end }}
              </template>

              <template #cell(start_date)="data">
                {{ data.item.start_date | formatDate }}
              </template>

              <template #cell(end_date)="data">
                {{ data.item.end_date | formatDate }}
              </template>

              <template #cell(actions)="data">
                <b-dropdown class="m-0" variant="link" right toggle-class="text-decoration-none" no-caret>
                  <template #button-content>
                    <i class="fas fa-ellipsis-v" />
                  </template>
                  <b-dropdown-item-button class="full-nested-btn"
                                          @click="$router.push({ name: 'update-social-booking-slot', params: { prisonId: data.item.prison_id, bookingSlotId: data.item._id }})"
                  >
                    Edit
                  </b-dropdown-item-button>
                  <b-dropdown-item-button class="full-nested-btn"
                                          @click="deleteSlot(data.item)"
                  >
                    Remove
                  </b-dropdown-item-button>
                </b-dropdown>
              </template>
            </b-table>
            <b-row>
              <b-col md="6" class="my-1">
                <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" class="my-0" />
              </b-col>
            </b-row>
          </b-container>
          <p v-if="bookingSlots.length === 0 && !calendarView" class="body-text text-center mt-5">
            No Booking Slots
          </p>
          <div v-if="calendarView">
            <social-booking-slots-calendar
              :slots="filteredBookingSlots"
              @slot-delete="deleteSlot($event)"
              @slot-edit="editSlot($event)"
            />
          </div>

        </section>
        <div v-else-if="loading" class="mt-1">
          <b-spinner class="d-block" variant="primary" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.booking-slots-page {
  span[cy="calendar-view"] {
    @include mobile() {
      display: none !important;
    }
  }
}
</style>
